import {initSocialLinksSuggestionsDropdown} from '@Global/email-maker';
import type {Component, Editor} from 'grapesjs';
import type {PrimaryConfigOptions} from '../index';
import type {SocialLinkData, SocialLinkDropdownOptions, SocialLinkInputCustomHandlers} from '../email-maker.types';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {
  getSanitizedUrl,
  addHttpsToUrl,
  addMailToToEmail,
  addTelToPhoneNumber,
  getUrlHasHttps,
  getEmailHasMailTo,
  getPhoneHasTel,
  isValidEmail,
  isValidPhoneNumber,
  updateInputValueForSocialLinkInputElement,
  htmlToElements,
} from '../email-maker-library';

const DEFAULT_SOCIAL_IDS = ['facebook', 'instagram', 'x', 'pinterest'];

interface SocialLinksAPIResponse {
  status: string;
  data: {
    socialLinks: SocialLinkData[];
  };
}

interface CreateData {
  props: {
    component?: Component;
  };
}

interface SortableEvent {
  item: HTMLElement;
  oldIndex: number;
  newIndex: number;
}

interface SortableLibrary {
  create: (
    container: HTMLElement,
    options: {
      selectedClass: string;
      handle: string;
      onStart: (evt: SortableEvent) => void;
      onEnd: (evt: SortableEvent) => void;
    }
  ) => void;
}

declare global {
  interface Window {
    Sortable: SortableLibrary;
  }
}

export default (opt: PrimaryConfigOptions): void => {
  const editor: Editor = opt.editor;
  const styleManager = editor.StyleManager;

  styleManager.addType(window.PMW.emails.styles.ID_SOCIAL_LINKS, {
    /**
     * Create the social links style manager element
     */
    create: (data: CreateData): HTMLElement | null => {
      const {props} = data;
      props.component = props.component ?? editor.getSelected();
      const iconStyle: string =
        (opt.editor.runCommand(window.PMW.emails.commands.GET_COMPONENT_STYLE, {component: props.component, property: 'icon-style'}) as string) ?? 'original';

      const el = `<div class="js-social-links-box social-links-box">
                                    <div id="js-sortable-social-links" class="js-socials-container socials-container"></div>
                                    <div class="js-add-social-link add-social-buttons">
                                        <i class="icon-plus pmw-icon size-icon-16 content-body"></i>
                                    </div>
                                    <div class="links-dialog-container js-links-dialog-container"></div>
                                </div>`;
      const elNodes: HTMLElement = htmlToElements(el);
      const elObj = $(elNodes);
      const cacheKey = 'social-links-html-v3';
      const selected: Component | undefined = props.component;

      if (!selected) {
        return null;
      }

      window.PMW.onClick(elObj, '.js-social-link-item', function (this: HTMLElement): void {
        const t = $(this);
        const logoURL: string = t.attr('data-logo-url') ?? '';
        const baseURL: string = t.attr('data-base-url') ?? '';
        const id: string = t.attr('data-id') ?? '';
        const name: string = t.attr('data-name') ?? '';
        const type: string = t.attr('data-type') ?? '';
        const isDisabled: string = t.attr('disabled') ?? '';
        const platformURL: string = t.attr('data-placeholder') ?? '';

        if (isDisabled) {
          return;
        }

        $(elObj).find(`[data-name="${name}"]`).removeAttr('disabled').removeAttr('title');
        t.attr('disabled', 'true').attr('title', window.i18next.t('pmwjs_social_link_already_added'));

        addSocialLinkButtonHTML(elObj, selected, logoURL, baseURL, name, id, iconStyle, platformURL);
        addSocialLinkComponent(selected, logoURL, baseURL, name, id, type, platformURL);
      });

      window.PMW.onClick(elObj, '.js-add-social-link', function (): void {
        const dialog = elObj.find('.js-links-dialog-container');
        const addBtn = elObj.find('.add-social-buttons');
        addBtn.toggleClass('-open');
        dialog.toggleClass('-open');
      });

      if (window.PMW.getCacheItem(cacheKey)) {
        const dialogContainer = elNodes.querySelector('.js-links-dialog-container');
        const socialLinksDialog = getSocialLinksDialog(window.PMW.getCacheItem(cacheKey) as SocialLinkData[], iconStyle);
        dialogContainer?.appendChild(socialLinksDialog);
        addDefaultLinks(selected, $(elNodes));
      } else {
        $.get(window.PMW.util.site_url('emailmaker/getSocialLinksURLs'), {}, function (r: SocialLinksAPIResponse) {
          if (r.status === 'success') {
            window.PMW.setCacheItem(cacheKey, r.data.socialLinks);
            const socialLinksDialog = getSocialLinksDialog(r.data.socialLinks, iconStyle);
            const dialogContainer = elNodes.querySelector('.js-links-dialog-container');
            dialogContainer?.appendChild(socialLinksDialog);
            addDefaultLinks(selected, $(elNodes));
          }
        });
      }

      addExistingSocialLinksToStyleManager(elObj, selected, iconStyle);
      disableExistingLinksInDialog(selected, elObj.find('.js-links-dialog-container'));

      return elNodes;
    },
    emit: (_data: unknown, ..._args: unknown[]): void => {
      // This function is intentionally empty as events are handled elsewhere
    },
    update: (_data: unknown): void => {
      // This function is intentionally empty as updates are handled elsewhere
    },
  });

  function getSocialLinksDialog(socialLinksData: SocialLinkData[], originalIconStyle: string): HTMLElement {
    const HTTPS_PREFIX_LENGTH = 8;
    const socialLinkList = document.createElement('ul');
    socialLinkList.className = 'social-link-items-list bg-neutral-0 radius-8 spacing-p-3 border-s-standard';

    let iconStyle = originalIconStyle;
    if (iconStyle === 'none') {
      iconStyle = 'original';
    }

    socialLinksData.forEach((socialLink: SocialLinkData) => {
      const listItem = document.createElement('li');
      listItem.className = 'social-link-item js-social-link-item spacing-m-b-2 radius-8 bg-neutral-0 flexbox flex-content-start flex-items-start';
      listItem.setAttribute('data-id', socialLink.id);
      listItem.setAttribute('data-logo-url', socialLink.logos[iconStyle]);
      listItem.setAttribute('data-base-url', '');
      listItem.setAttribute('data-name', socialLink.name);

      const url: string = socialLink.baseURL;
      const placeholderURL: string = url.startsWith('https://') ? url.substring(HTTPS_PREFIX_LENGTH) : url;
      listItem.setAttribute('data-placeholder', placeholderURL);

      const img = document.createElement('img');
      img.className = 'social-link-img spacing-m-r-2';
      img.setAttribute('src', socialLink.logos[iconStyle]);
      img.setAttribute('alt', socialLink.name);

      const name = document.createElement('span');
      name.className = 'social-link-text body-md-regular content-body';
      name.textContent = socialLink.name;

      listItem.appendChild(img);
      listItem.appendChild(name);
      socialLinkList.appendChild(listItem);
    });

    return socialLinkList;
  }

  function disableExistingLinksInDialog(component: Component, dialogContainer: any): void {
    const existingImages: Component[] = component.findType(window.PMW.emails.components.social.PMW_SOCIALS_IMAGE);
    const existingLinkIds = existingImages.map((image: Component) => {
      const attributes = image.getAttributes() ?? {};
      return attributes['data-id'];
    });

    existingLinkIds.map((id) => {
      dialogContainer.find(`.js-social-link-item[data-id="${id}"]`).attr('disabled', 'true').attr('title', window.i18next.t('pmwjs_social_link_already_added'));
    });
  }

  function addDefaultLinks(component: Component, container: any): void {
    const isEmpty: boolean = component.findType(window.PMW.emails.components.social.PMW_SOCIALS_ITEM).length === 0;
    if (isEmpty) {
      DEFAULT_SOCIAL_IDS.forEach((id: string) => {
        const linkItem = container.find(`.js-social-link-item[data-id="${id}"]`)[0];
        if (linkItem) {
          linkItem.click();
        }
      });
    }
  }

  function canAddSocialLinkComponent(component: Component, name: string): boolean {
    const imageIcons: Component[] = component.findType(window.PMW.emails.components.social.PMW_SOCIALS_IMAGE);
    const filteredIcons: Component[] = imageIcons.filter((icon: Component) => {
      const attrs = icon.getAttributes() || {};
      return attrs['data-platform-name'] === name;
    });

    return filteredIcons.length === 0;
  }

  function canAddSocialLinkHTML(container: any, id: string): boolean {
    return $(container).find(`.js-social-link-input[data-id="${id}"]`).length === 0;
  }

  function addExistingSocialLinksToStyleManager(elObj: any, component: Component, iconStyle: string): void {
    const socialIcons: Component[] | undefined = component?.findType(window.PMW.emails.components.social.PMW_SOCIALS_IMAGE);
    socialIcons?.forEach((icon: Component) => {
      const attributes = icon.getAttributes();
      const id: string = attributes['data-id'] as string;
      const logoURL: string = attributes['data-logo-url'] as string;
      const baseURL: string = attributes['data-base-url'] as string;
      const platFormUrl: string = attributes['data-platform-url'] as string;
      const platformName: string = attributes['data-platform-name'] as string;

      if (doesIconHaveInconsistentStyle(logoURL, iconStyle)) {
        // handle case for old social links with different IconStyles
        handleInconsistentIconStylesForOldSocialLinks();
      }
      addSocialLinkButtonHTML(elObj, component, logoURL, baseURL, platformName, id, iconStyle, platFormUrl);
    });
  }

  function addSocialLinkComponent(component: Component, logoURL: string, baseURL: string, name: string, id: string, type: string, platformURL: string): void {
    const canAdd: boolean = canAddSocialLinkComponent(component, name);
    const existingImage: Component[] = component.find(`[data-platform-name="${name}"]`);
    const newCompData = {
      type: window.PMW.emails.components.social.PMW_SOCIALS_IMAGE,
      stylable: true,
      removable: false,
      hoverable: false,
      selectable: false,
      draggable: false,
      highlightable: false,
      attributes: {
        src: logoURL,
        alt: name,
        'data-platform-name': name,
        'data-id': id,
        'data-logo-url': logoURL,
        'data-base-url': canAdd ? baseURL : (existingImage[0].getAttributes()['data-base-url'] as string),
        'data-gjs-selectable': 'false',
        'data-platform-url': platformURL,
        'data-gjs-hoverable': 'false',
      },
    };
    const socialPlatformImage: Component = (canAdd ? component.append(newCompData)[0] : existingImage[0].replaceWith(newCompData)) as Component;

    // Tracking temporarily disabled on 8th Nov 2023
    // window.PMW.gtm.trackGA4CustomEvent('EmailMaker_SocialMediaLink', {[PMW.gtm.GA4_EVENT_PARAM_NAMES.SOCIAL_MEDIA_PLATFORM]: name});

    editor.runCommand(window.PMW.emails.commands.ADD_COMPONENT_ANCHOR, {
      component: socialPlatformImage,
      url: baseURL,
      customLinkComponent: window.PMW.emails.components.social.PMW_SOCIALS_ITEM,
    });

    updateSocialLinkStyles(component);
  }

  function updateSocialLinkStyles(component: Component): void {
    const style = component.getStyle();

    if (style?.['pmw-icon-margin']) {
      editor.runCommand(window.PMW.emails.commands.UPDATE_SOCIAL_LINK_SPACING, {gap: parseInt(style['pmw-icon-margin'] as string)});
    }

    if (style?.['pmw-icon-size']) {
      editor.runCommand(window.PMW.emails.commands.UPDATE_SOCIAL_LINK_SIZE, {size: parseInt(style['pmw-icon-size'] as string)});
    }
  }

  function createSortableLinkFields(container: any, selected: Component): void {
    const sortableLib = window.Sortable;
    if (sortableLib) {
      sortableLib.create(container.get(0), {
        selectedClass: 'sortable-selected',
        handle: '.js-slide-dragger',
        onStart: function (evt: SortableEvent): void {
          triggerClickForSuggestionsDropdownClose(evt.item);
        },
        onEnd: function (evt: SortableEvent): void {
          let index = 0;

          selected.find(`[data-gjs-type="${window.PMW.emails.components.social.PMW_SOCIALS_ITEM}"]`).forEach((component: Component) => {
            if (index === evt.oldIndex) {
              const parent = component.parent();
              component.remove({temporary: true});
              parent?.append(component, {at: evt.newIndex});
            }
            index += 1;
          });

          evt.item.style.transform = '';
        },
      });
    }
  }

  function triggerClickForSuggestionsDropdownClose(draggableContainer: HTMLElement): void {
    draggableContainer.click();
  }

  function addSocialLinkButtonHTML(elObj: any, selected: Component, logoURL: string, baseURL: string, name: string, id: string, iconStyle: string, placeholder: string): void {
    const container = elObj.find('.js-socials-container');
    const dialog = elObj.find('.js-links-dialog-container');
    const html = `<div class="js-draggable-social-link social-draggable-container">
                                <div class="social-link-input-container flex-column-justify-center">               
                                    <div class="flex-center spacing-p-t-2 spacing-p-b-2">
                                        <i class="pmw-icon js-slide-dragger icon-draggable social-link-drag-handler size-icon-16 spacing-m-r-2 spacing-m-l-0"></i>
                                        <div class="social-link-input-img-container"><img class="social-link-input-img ${iconStyle}" src="${logoURL}" alt="${name}"/></div>
                                        <div class="js-social-link-input flex-1" data-id="${id}"></div>
                                        <i class="pmw-icon icon-delete delete-icon js-delete-social-link size-icon-16 -square -ghost" data-id="${id}"></i>
                                    </div>
                                    <div class="error-message-container flexbox spacing-m-l-18 _hidden">
                                         <i class="pmw-icon icon-exclamation-triangle size-icon-16 _unmargin content-danger"></i>
                                         <span class="invalid-url-message body-sm-regular content-danger">${window.i18next.t('pmwjs_invalid_url')}</span>
                                    </div>
                                </div>
                            </div>`;

    dialog.addClass(iconStyle);
    if (canAddSocialLinkHTML(container, id)) {
      const newEl = document.createElement('div');
      newEl.innerHTML = html;
      container[0]?.appendChild(newEl);
      const inputElContainer = newEl.querySelector(`.js-social-link-input[data-id="${id}"]`) as HTMLElement;
      renderSocialLinkInputSuggestionsDropdown(inputElContainer, id, baseURL, placeholder);
    } else {
      const existingInputEl = $(container).find(`.js-social-link-input[data-id="${id}"]`).find('input');

      if (existingInputEl.length !== 0) {
        const existingURL = existingInputEl.val();
        const updatedHtml = html.replace(baseURL, existingURL?.toString() ?? '');
        existingInputEl.parents('.js-draggable-social-link').replaceWith(updatedHtml);
      }
    }

    if (!container.attr('data-init')) {
      container.attr('data-init', 1);

      window.PMW.onClick(container, '.js-delete-social-link', function (this: HTMLElement): void {
        const t = $(this);
        const id = t.attr('data-id');

        selected.find(`[data-gjs-type="${window.PMW.emails.components.social.PMW_SOCIALS_IMAGE}"][data-id="${id}"]`).forEach((component: Component) => {
          component.parent()?.remove();
        });

        t.parents('.js-draggable-social-link').remove();
        dialog.find(`[data-id="${id}"]`).removeAttr('disabled').removeAttr('title');
      });

      if (typeof window.Sortable === 'undefined') {
        window.PMW.getJsAssetGroup('sortableLib', function (): void {
          createSortableLinkFields(container, selected);
        });
      } else {
        createSortableLinkFields(container, selected);
      }
    }
  }

  /**
   * Renders React component for social links suggestions
   */
  function renderSocialLinkInputSuggestionsDropdown(contentContainer: HTMLElement, id: string, inputValue: string, inputPlaceholder: string): void {
    const socialLinkInputCustomHandlers: SocialLinkInputCustomHandlers = {
      onInputFocus: onCustomInputFocus,
      onInputBlur: onCustomInputBlur,
    };

    const dropdownOpts: SocialLinkDropdownOptions = {
      platform: id,
      socialLinkInputCustomHandlers: socialLinkInputCustomHandlers,
      inputFieldProps: {
        inputClassName: 'social-link-input-url js-social-link-input-url border-s-darker radius-4 body-sm-regular content-body spacing-m-r-1',
        placeholder: inputPlaceholder ?? window.i18next.t('pmwjs_add_profile_url'),
        input: inputValue,
        size: InputFieldSize.XSMALL,
      },
      inputSuggestionsDropdownProps: {
        popupClassName: 'social-link-suggestions-dropdown',
        popupAlignment: ALIGNMENT_TYPE.BOTTOM_END,
        popupPosition: DROPDOWN_POSITION.FIXED,
        truncateText: true,
        showSuggestionAsTitleOnHover: true,
      },
      inputId: `social-link-input-${id}`,
      dropdownId: `social-link-dropdown-${id}`,
    };

    initSocialLinksSuggestionsDropdown(contentContainer, dropdownOpts);
  }

  /**
   * Shows or hides invalid URL error message
   */
  function doShowInvalidUrlMessage(urlContainer: HTMLInputElement | null, showError: boolean): void {
    if (!urlContainer) {
      return;
    }

    const socialLinkContainer = urlContainer.closest('.social-link-input-container') as HTMLElement;
    const invalidUrlMessage = socialLinkContainer?.querySelector('.error-message-container') as HTMLElement;

    if (invalidUrlMessage) {
      if (showError) {
        invalidUrlMessage.classList.remove('_hidden');
        urlContainer.classList.add('-error');
      } else {
        invalidUrlMessage.classList.add('_hidden');
        urlContainer.classList.remove('-error');
      }
    }
  }

  /**
   * Checks if icon has inconsistent style for old social links
   */
  function doesIconHaveInconsistentStyle(logoURL: string, iconStyle: string): boolean {
    const logoUrlParts = logoURL.split('/social-icons/');
    if (logoUrlParts.length > 1) {
      const iconPath = logoUrlParts[1];
      const iconDirectory = iconPath.split('/')[0];

      if (iconDirectory !== iconStyle) {
        return true;
      }
    }

    return false;
  }

  function handleInconsistentIconStylesForOldSocialLinks(): void {
    opt.editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {component: editor.getSelected(), property: 'icon-style', value: 'none'});
  }

  /**
   * Custom onInputFocus passed to React InputField component that hides the invalid URL error state on focus
   */
  function onCustomInputFocus(dataId: string, _value: string): void {
    const inputElContainer = document.querySelector(`.js-social-link-input[data-id="${dataId}"] input`) as HTMLInputElement;
    doShowInvalidUrlMessage(inputElContainer, false);
  }

  /**
   * Custom onInputBlur passed to React InputField component that takes the new input value
   * and updates the href for the social link icon in the email template
   */
  function onCustomInputBlur(dataId: string, value: string): void {
    const inputElContainer = document.querySelector(`.js-social-link-input[data-id="${dataId}"] input`) as HTMLInputElement;
    let url: string = getSanitizedUrl(value.trim());

    if (url === '' || editor.runCommand(window.PMW.emails.commands.IS_VALID_FOR_LINK, {url: url})) {
      doShowInvalidUrlMessage(inputElContainer, false);
    } else {
      doShowInvalidUrlMessage(inputElContainer, true);
      return;
    }

    const urlHasHttps: boolean = getUrlHasHttps(url);
    const isUrlEmail: boolean = isValidEmail(url);
    const isUrlPhone: boolean = isValidPhoneNumber(url);
    const emailHasMailTo: boolean = getEmailHasMailTo(url);
    const phoneHasTel: boolean = getPhoneHasTel(url);
    const urlNeedsUpdate = Boolean(url && !urlHasHttps && !emailHasMailTo && !phoneHasTel);

    if (isUrlEmail && !emailHasMailTo) {
      url = addMailToToEmail(url);
    } else if (isUrlPhone && !phoneHasTel) {
      url = addTelToPhoneNumber(url);
    } else if (url && !urlHasHttps && !isUrlEmail && !isUrlPhone) {
      url = addHttpsToUrl(url);
    }

    if (urlNeedsUpdate) {
      const jqueryLikeObject = {
        val: (newValue?: string) => {
          if (newValue !== undefined) {
            inputElContainer.value = newValue;
          }
          return inputElContainer.value;
        },
      };
      updateInputValueForSocialLinkInputElement(jqueryLikeObject as any, url);
    }

    editor
      .getSelected()
      ?.find(`[data-gjs-type="${window.PMW.emails.components.social.PMW_SOCIALS_IMAGE}"][data-id="${dataId}"]`)
      .forEach((component: Component) => {
        const currentBaseUrl: string = component.getAttributes()['data-base-url'] as string;
        const hasUrlUpdated: boolean = currentBaseUrl !== url;

        if (!hasUrlUpdated) {
          return;
        }

        component.addAttributes({'data-base-url': url});
        const parent = component.parent();
        parent?.addAttributes({
          href: url,
          'data-url': url,
          'data-base-url': url,
        });
      });
  }
};
